/**
 * Marketing Pixel Tracking Utility
 *
 * Manages initialization and event tracking for marketing pixels:
 * - Meta Pixel (Facebook)
 * - X (Twitter) Pixel
 * - TikTok Pixel
 *
 * Only loads and tracks if user has given marketing consent via cookie consent banner.
 * Integrates with consent utility to avoid circular dependencies.
 *
 * Environment Variables:
 * - VITE_META_PIXEL_ID: Meta Pixel (Facebook) ID (optional)
 * - VITE_TWITTER_PIXEL_ID: X (Twitter) Pixel ID (optional)
 * - VITE_TIKTOK_PIXEL_ID: TikTok Pixel ID (optional)
 *
 * If pixel IDs are not provided, that pixel will be skipped during initialization.
 */

import { hasMarketingConsent } from "@/utils/consent";
import { logger } from "@/utils/logger";
import { env } from "@/config/env";

// Pixel IDs (from environment configuration)
const META_PIXEL_ID = env.metaPixelId;
const TWITTER_PIXEL_ID = env.twitterPixelId;
const TIKTOK_PIXEL_ID = env.tiktokPixelId;

// Track initialization state
let isMetaPixelLoaded = false;
let isTwitterPixelLoaded = false;
let isTikTokPixelLoaded = false;

/**
 * Check if Meta Pixel is loaded
 */
function isMetaPixelInitialized(): boolean {
  return typeof window !== "undefined" && typeof window.fbq !== "undefined";
}

/**
 * Check if Twitter Pixel is loaded
 */
function isTwitterPixelInitialized(): boolean {
  return typeof window !== "undefined" && typeof window.twq !== "undefined";
}

/**
 * Check if TikTok Pixel is loaded
 */
function isTikTokPixelInitialized(): boolean {
  return typeof window !== "undefined" && typeof window.ttq !== "undefined";
}

/**
 * Initialize Meta Pixel (Facebook)
 */
function initMetaPixel(): void {
  if (!META_PIXEL_ID) {
    logger.info("[Marketing Pixels] Meta Pixel ID not configured, skipping");
    return;
  }

  if (isMetaPixelInitialized()) {
    isMetaPixelLoaded = true;
    return;
  }

  if (isMetaPixelLoaded) {
    return;
  }

  try {
    // Initialize fbq function
    // Create object that matches Facebook Pixel's fbq interface
    const queue: unknown[][] = [];
    const fbqFunction = function (command: string, ...args: unknown[]) {
      const obj = window.fbq;
      if (obj?.callMethod) {
        obj.callMethod(command, ...args);
      } else {
        queue.push([command, ...args]);
      }
    };

    const fbqObj: typeof window.fbq = Object.assign(fbqFunction, {
      push: fbqFunction,
      loaded: true,
      version: "2.0",
      queue: queue,
    });

    window.fbq = fbqObj;

    // Load Meta Pixel script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    // Initialize pixel
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");

    isMetaPixelLoaded = true;
    logger.info("[Marketing Pixels] Meta Pixel initialized");
  } catch (error) {
    logger.error("[Marketing Pixels] Failed to initialize Meta Pixel:", error);
  }
}

/**
 * Initialize Twitter Pixel (X)
 */
function initTwitterPixel(): void {
  if (!TWITTER_PIXEL_ID) {
    logger.info("[Marketing Pixels] Twitter Pixel ID not configured, skipping");
    return;
  }

  if (isTwitterPixelInitialized()) {
    isTwitterPixelLoaded = true;
    return;
  }

  if (isTwitterPixelLoaded) {
    return;
  }

  try {
    // Initialize twq function
    const queue: unknown[][] = [];
    const twqFunction = function (command: string, ...args: unknown[]) {
      const obj = window.twq;
      const objQueue = obj?.queue || queue;
      objQueue.push([command, ...args]);
    };

    const twqObj: typeof window.twq = Object.assign(twqFunction, {
      queue: queue,
    });

    window.twq = twqObj;

    // Load Twitter Pixel script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://static.ads-twitter.com/uwt.js";
    document.head.appendChild(script);

    // Initialize pixel
    window.twq("init", TWITTER_PIXEL_ID);
    window.twq("track", "PageView");

    isTwitterPixelLoaded = true;
    logger.info("[Marketing Pixels] Twitter Pixel initialized");
  } catch (error) {
    logger.error("[Marketing Pixels] Failed to initialize Twitter Pixel:", error);
  }
}

/**
 * Initialize TikTok Pixel
 */
function initTikTokPixel(): void {
  if (!TIKTOK_PIXEL_ID) {
    logger.info("[Marketing Pixels] TikTok Pixel ID not configured, skipping");
    return;
  }

  if (isTikTokPixelInitialized()) {
    isTikTokPixelLoaded = true;
    return;
  }

  if (isTikTokPixelLoaded) {
    return;
  }

  try {
    // Initialize ttq as array queue (TikTok pixel standard initialization)
    // The TikTok script will convert this array to an object with methods
    window.ttq = window.ttq || [];

    // Queue the load and page calls - TikTok script will process them
    // Using type assertion since TikTok's script transforms the array to object
    const ttqArray = window.ttq as unknown[];
    ttqArray.push(["load", TIKTOK_PIXEL_ID]);
    ttqArray.push(["page"]);

    // Load TikTok Pixel script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${TIKTOK_PIXEL_ID}&lib=SDK`;
    document.head.appendChild(script);

    isTikTokPixelLoaded = true;
    logger.info("[Marketing Pixels] TikTok Pixel initialized");
  } catch (error) {
    logger.error("[Marketing Pixels] Failed to initialize TikTok Pixel:", error);
  }
}

/**
 * Initialize all marketing pixels
 * Only loads if marketing consent is given
 */
export function initMarketingPixels(): void {
  // Don't initialize if consent not given
  if (!hasMarketingConsent()) {
    logger.info(
      "[Marketing Pixels] Marketing consent not given, skipping initialization"
    );
    return;
  }

  // Initialize each pixel
  initMetaPixel();
  initTwitterPixel();
  initTikTokPixel();
}

/**
 * Track page view across all marketing pixels
 */
export function trackPixelPageView(_path: string, _title?: string): void {
  if (!hasMarketingConsent()) {
    return;
  }

  // Meta Pixel - only track if pixel ID is configured and initialized
  if (META_PIXEL_ID && isMetaPixelInitialized() && typeof window.fbq === "function") {
    try {
      window.fbq("track", "PageView");
    } catch (error) {
      logger.error("[Marketing Pixels] Failed to track Meta Pixel page view:", error);
    }
  }

  // Twitter Pixel - only track if pixel ID is configured and initialized
  if (
    TWITTER_PIXEL_ID &&
    isTwitterPixelInitialized() &&
    typeof window.twq === "function"
  ) {
    try {
      window.twq("track", "PageView");
    } catch (error) {
      logger.error("[Marketing Pixels] Failed to track Twitter Pixel page view:", error);
    }
  }

  // TikTok Pixel - only track if pixel ID is configured and initialized
  if (TIKTOK_PIXEL_ID && isTikTokPixelInitialized() && window.ttq) {
    try {
      // TikTok pixel can be array-based (queue) or object-based (initialized)
      if (Array.isArray(window.ttq)) {
        // Queue-based: push page event to queue
        window.ttq.push(["page"]);
      } else if (
        typeof window.ttq === "object" &&
        typeof window.ttq.page === "function"
      ) {
        // Object-based: call page method directly
        window.ttq.page();
      }
    } catch (error) {
      logger.error("[Marketing Pixels] Failed to track TikTok Pixel page view:", error);
    }
  }
}

/**
 * Track custom event across all marketing pixels
 */
export function trackPixelEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!hasMarketingConsent()) {
    return;
  }

  // Meta Pixel
  if (isMetaPixelInitialized() && typeof window.fbq === "function") {
    try {
      window.fbq("track", eventName, params);
    } catch (error) {
      logger.error(
        `[Marketing Pixels] Failed to track Meta Pixel event ${eventName}:`,
        error
      );
    }
  }

  // Twitter Pixel
  if (isTwitterPixelInitialized() && typeof window.twq === "function") {
    try {
      window.twq("track", eventName, params);
    } catch (error) {
      logger.error(
        `[Marketing Pixels] Failed to track Twitter Pixel event ${eventName}:`,
        error
      );
    }
  }

  // TikTok Pixel
  if (isTikTokPixelInitialized() && window.ttq) {
    try {
      // TikTok pixel can be array-based (queue) or object-based (initialized)
      if (Array.isArray(window.ttq)) {
        // Queue-based: push track event to queue
        window.ttq.push(["track", eventName, params]);
      } else if (
        typeof window.ttq === "object" &&
        typeof window.ttq.track === "function"
      ) {
        // Object-based: call track method directly
        window.ttq.track(eventName, params);
      }
    } catch (error) {
      logger.error(
        `[Marketing Pixels] Failed to track TikTok Pixel event ${eventName}:`,
        error
      );
    }
  }
}

/**
 * Track Meta Pixel event
 */
export function trackMetaEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!hasMarketingConsent() || !isMetaPixelInitialized()) {
    return;
  }

  try {
    if (typeof window.fbq === "function") {
      window.fbq("track", eventName, params);
    }
  } catch (error) {
    logger.error(
      `[Marketing Pixels] Failed to track Meta Pixel event ${eventName}:`,
      error
    );
  }
}

/**
 * Track Twitter Pixel event
 */
export function trackTwitterEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!hasMarketingConsent() || !isTwitterPixelInitialized()) {
    return;
  }

  try {
    if (typeof window.twq === "function") {
      window.twq("track", eventName, params);
    }
  } catch (error) {
    logger.error(
      `[Marketing Pixels] Failed to track Twitter Pixel event ${eventName}:`,
      error
    );
  }
}

/**
 * Track TikTok Pixel event
 */
export function trackTikTokEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!hasMarketingConsent() || !isTikTokPixelInitialized() || !window.ttq) {
    return;
  }

  try {
    // TikTok pixel can be array-based (queue) or object-based (initialized)
    if (Array.isArray(window.ttq)) {
      // Queue-based: push track event to queue
      window.ttq.push(["track", eventName, params]);
    } else if (typeof window.ttq === "object" && typeof window.ttq.track === "function") {
      // Object-based: call track method directly
      window.ttq.track(eventName, params);
    }
  } catch (error) {
    logger.error(
      `[Marketing Pixels] Failed to track TikTok Pixel event ${eventName}:`,
      error
    );
  }
}

// Extend Window interface for pixel functions
declare global {
  interface Window {
    fbq?: {
      (command: string, ...args: unknown[]): void;
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[][];
      loaded: boolean;
      version: string;
    };
    twq?: {
      (command: string, ...args: unknown[]): void;
      queue: unknown[][];
    };
    ttq?:
      | unknown[] // Array-based queue initially
      | {
          // Methods after initialization
          page?: () => void;
          track?: (event: string, data?: Record<string, unknown>) => void;
          load?: (pixelId: string, lib?: string) => void;
          methods?: string[];
          setAndDefer?: (t: { [key: string]: unknown }, e: string) => void;
          instance?: (t: string) => unknown;
          _i?: { [key: string]: unknown[] };
          _t?: { [key: string]: number };
          _o?: { [key: string]: unknown };
          // Allow any other properties
          [key: string]: unknown;
        };
  }
}
